import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, MailPlus } from 'lucide-react';
import { chargerConnectivityLabel, type NetworkSubmissionAdmin } from '@evi/shared';
import {
  listNetworkSubmissionsAdmin,
  decideNetworkSubmission,
  issueSubmissionInvite,
  listSubmissionInvites,
  revokeSubmissionInvite,
  type SubmissionInviteAdmin,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { Modal } from '@/components/ui/Modal';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './NetworkSubmissions.module.css';
import { HostCampaignReview } from './HostCampaignReview';

/**
 * Network Submissions review queue: installers / SPs / fleet managers
 * propose locations + chargers; approval here opens commissioning
 * (the location and commissioning units, with passwords only on the
 * signed-in deployment page). A charger goes live after it connects,
 * passes its plug test, and EV Initiative approves the launch. Each
 * row shows the deployment's recorded connection choice. Blend of the
 * LeadsPage data pattern (filtered list + detail) and the OpsQueue
 * decision modal.
 *
 * External submitters get here by INVITE ONLY: the header issues
 * single-use magic links (14-day, revocable), the invites list below the
 * queue tracks them, and the needs_info decision can carry a fresh edit
 * link that reopens the same submission.
 */

const STATUS_LABEL: Record<NetworkSubmissionAdmin['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  needs_info: 'Needs info',
};

const ROLE_LABEL: Record<string, string> = {
  electrician: 'Electrician',
  sales_partner: 'Sales Partner',
  fleet_manager: 'Fleet manager',
  property_manager: 'Property manager',
  other: 'Other',
};

const CHIPS = ['pending', 'needs_info', 'approved', 'rejected', 'all'] as const;

export function SubmissionsPage() {
  const hostReviewer = useAuthStore(s => s.user?.role === 'super_admin');
  useRecentRoute('Network Submissions');
  const qc = useQueryClient();
  const [chip, setChip] = useState<(typeof CHIPS)[number]>('pending');
  const [detail, setDetail] = useState<NetworkSubmissionAdmin | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: submissions = [], isLoading, error } = useQuery({
    queryKey: ['network-submissions', chip],
    queryFn: () => listNetworkSubmissionsAdmin(chip === 'all' ? undefined : chip),
    staleTime: 15_000,
  });

  const columns: Column<NetworkSubmissionAdmin>[] = [
    {
      key: 'location',
      header: 'Location',
      mobile: 'primary',
      cell: (s) => (
        <>
          {s.location.name}
          {s.duplicateFlags.length > 0 && s.status === 'pending' && (
            <span className={styles.dupBadge}>
              {s.duplicateFlags.length} dup{s.duplicateFlags.length === 1 ? '' : 's'}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'where',
      header: 'City',
      mobile: 'secondary',
      cell: (s) => `${s.location.address.city}, ${s.location.address.state}`,
    },
    { key: 'type', header: 'Type', mobile: 'body', cell: (s) => s.location.type },
    {
      key: 'chargers',
      header: 'Chargers',
      align: 'right',
      mobile: 'body',
      cell: (s) => String(s.chargers.length),
    },
    {
      key: 'connection',
      header: 'Connection',
      mobile: 'body',
      cell: (s) => chargerConnectivityLabel(s.connectivity),
    },
    {
      key: 'from',
      header: 'Submitted by',
      mobile: 'body',
      cell: (s) => `${s.contact.name} (${ROLE_LABEL[s.contact.role] ?? s.contact.role})`,
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (s) => (
        <span className={`${styles.status} ${styles[`status_${s.status}`]}`}>
          {STATUS_LABEL[s.status]}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Inbox size={18} /></div>
          <div>
            <h1 className={styles.title}>Network Submissions</h1>
            <p className={styles.subtitle}>
              Proposed locations and chargers. Approval opens commissioning. A charger goes live after it connects, passes its plug test, and EV Initiative approves the launch.
            </p>
          </div>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setInviteOpen(true)}>
          <MailPlus size={15} /> Invite a submitter
        </button>
      </div>

      {hostReviewer && <HostCampaignReview />}
      <div className={styles.chips}>
        {CHIPS.map((c) => (
          <button
            key={c}
            type="button"
            className={`${styles.chip} ${chip === c ? styles.chipActive : ''}`}
            onClick={() => setChip(c)}
          >
            {c === 'all' ? 'All' : STATUS_LABEL[c]}
          </button>
        ))}
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error instanceof Error ? error.message : 'Failed to load submissions'}
        </div>
      )}

      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={submissions}
          rowKey={(s) => s.id}
          onRowClick={(s) => setDetail(s)}
          emptyMessage={
            <div className={styles.empty}>
              {chip === 'pending' ? 'Nothing needs review.' : 'No submissions here.'}
            </div>
          }
        />
      )}

      <InvitesSection />

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} />}

      {detail && (
        <SubmissionDetailModal
          submission={detail}
          onClose={() => setDetail(null)}
          onDecided={(updated) => {
            qc.invalidateQueries({ queryKey: ['network-submissions'] });
            qc.invalidateQueries({ queryKey: ['submission-invites'] });
            setDetail(updated);
          }}
        />
      )}
    </div>
  );
}

// ── Invites (issue + list + revoke) ─────────────────────────────────

/** Invite statuses ride the existing pill palette: consumed reads like
 *  approved, expired like needs-info, revoked like rejected. */
const INVITE_PILL: Record<SubmissionInviteAdmin['status'], string> = {
  pending: 'status_pending',
  used: 'status_approved',
  expired: 'status_needs_info',
  revoked: 'status_rejected',
};

function InvitesSection() {
  const qc = useQueryClient();
  const [actionError, setActionError] = useState('');

  const { data: invites = [], isLoading } = useQuery({
    queryKey: ['submission-invites'],
    queryFn: listSubmissionInvites,
    staleTime: 15_000,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeSubmissionInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['submission-invites'] }),
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Revoke failed'),
  });

  const columns: Column<SubmissionInviteAdmin>[] = [
    { key: 'email', header: 'Invited email', mobile: 'primary', cell: (i) => i.email },
    {
      key: 'kind',
      header: 'Kind',
      mobile: 'body',
      cell: (i) => (i.resubmitOf ? 'Edit link (needs info)' : 'New submission'),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'secondary',
      cell: (i) => (
        <span className={`${styles.status} ${styles[INVITE_PILL[i.status]]}`}>
          {i.status === 'used' ? 'Used' : i.status === 'pending' ? 'Pending' : i.status === 'expired' ? 'Expired' : 'Revoked'}
        </span>
      ),
    },
    {
      key: 'expires',
      header: 'Expires',
      mobile: 'body',
      cell: (i) => new Date(i.expiresAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      mobile: 'body',
      cell: (i) => (
        i.status === 'pending' ? (
          <button
            type="button"
            className={styles.dangerBtn}
            style={{ padding: '4px 12px', fontSize: 12 }}
            disabled={revoke.isPending}
            onClick={() => { setActionError(''); revoke.mutate(i.id); }}
          >
            Revoke
          </button>
        ) : null
      ),
    },
  ];

  return (
    <div className={styles.detailSection}>
      <p className={styles.detailLabel}>Submission invites</p>
      {actionError && <div className={styles.errorBanner}>{actionError}</div>}
      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={invites}
          rowKey={(i) => i.id}
          emptyMessage={
            <div className={styles.empty}>
              No invites yet — external submitters can only get in through one.
            </div>
          }
        />
      )}
    </div>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const user = useAuthStore((st) => st.user);
  const isSuper = user?.role === 'super_admin';
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const issue = useMutation({
    mutationFn: () => issueSubmissionInvite({
      email: email.trim(),
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(isSuper && tenantId.trim() ? { tenantId: tenantId.trim() } : {}),
    }),
    onSuccess: ({ emailSent, invite }) => {
      qc.invalidateQueries({ queryKey: ['submission-invites'] });
      setResult(emailSent
        ? { ok: true, msg: `Invite emailed to ${invite.email}. The link works once and expires in 14 days.` }
        : { ok: false, msg: `Invite created, but the email to ${invite.email} FAILED to send. Revoke it below and re-issue.` });
    },
    onError: async (err) => {
      const httpErr = err as { response?: Response; message?: string };
      let msg = httpErr.message ?? 'Could not issue the invite';
      if (httpErr.response) {
        try {
          const j = await httpErr.response.clone().json() as { error?: { message?: string } };
          msg = j.error?.message ?? msg;
        } catch { /* keep generic */ }
      }
      setResult({ ok: false, msg });
    },
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <Modal isOpen onClose={onClose} title="Invite a submitter" size="md">
      <div className={styles.detail}>
        <p className={styles.fieldHint}>
          Sends a single-use magic link to register a location and its chargers.
          The link is personal to the email, expires in 14 days, and can be
          revoked from the invites list.
        </p>
        <div className={styles.field}>
          <span className={styles.label}>Recipient email</span>
          <input
            className={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="installer@example.com"
            autoFocus
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>
            Note <span className={styles.muted}>(optional — included in the invite email)</span>
          </span>
          <textarea
            className={styles.input}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
        </div>
        {isSuper && (
          <div className={styles.field}>
            <span className={styles.label}>
              Pre-bind tenant id <span className={styles.muted}>(optional — the approval&rsquo;s default target)</span>
            </span>
            <input
              className={styles.input}
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="ObjectId"
            />
          </div>
        )}

        {result && (
          <div className={result.ok ? styles.successBanner : styles.errorBanner}>{result.msg}</div>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            {result?.ok ? 'Done' : 'Close'}
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={issue.isPending || !emailValid || result?.ok === true}
            onClick={() => { setResult(null); issue.mutate(); }}
          >
            {issue.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Detail + decision modal ─────────────────────────────────────────

function SubmissionDetailModal({
  submission: s, onClose, onDecided,
}: {
  submission: NetworkSubmissionAdmin;
  onClose: () => void;
  onDecided: (updated: NetworkSubmissionAdmin) => void;
}) {
  const user = useAuthStore((st) => st.user);
  const isSuper = user?.role === 'super_admin';
  const [note, setNote] = useState('');
  const admittance = 'password' as const;
  const [tenantOverride, setTenantOverride] = useState('');
  // Edit link default: invited (public) submitters have NO other way back
  // in, so it defaults on; admin-app submitters can resubmit in-app.
  const [reinvite, setReinvite] = useState(s.source === 'public');
  const [submitError, setSubmitError] = useState('');
  const dupByIdentity = new Map(s.duplicateFlags.map((f) => [f.identity, f.kind]));

  const decide = useMutation({
    mutationFn: (action: 'approve' | 'reject' | 'needs_info') =>
      decideNetworkSubmission(s.id, {
        action,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(action === 'approve' ? { admittance } : {}),
        ...(action === 'approve' && isSuper && tenantOverride.trim()
          ? { tenantId: tenantOverride.trim() }
          : {}),
        ...(action === 'needs_info' ? { reinvite } : {}),
      }),
    onSuccess: (updated) => onDecided(updated),
    onError: async (err) => {
      const httpErr = err as { response?: Response; message?: string };
      let msg = httpErr.message ?? 'Decision failed';
      if (httpErr.response) {
        try {
          const j = await httpErr.response.clone().json() as { error?: { message?: string } };
          msg = j.error?.message ?? msg;
        } catch { /* keep generic */ }
      }
      setSubmitError(msg);
    },
  });

  const live = s.status === 'pending' || s.status === 'needs_info';

  return (
    <Modal isOpen onClose={onClose} title={s.location.name} size="lg">
      <div className={styles.detail}>
        <div className={styles.detailSection}>
          <p className={styles.detailLabel}>Status</p>
          <p className={styles.detailValue}>
            <span className={`${styles.status} ${styles[`status_${s.status}`]}`}>
              {STATUS_LABEL[s.status]}
            </span>
            {s.reviewerNote ? ` — ${s.reviewerNote}` : ''}
          </p>
        </div>

        <div className={styles.fieldGrid2}>
          <div className={styles.detailSection}>
            <p className={styles.detailLabel}>Submitted by</p>
            <p className={styles.detailValue}>
              {s.contact.name} ({ROLE_LABEL[s.contact.role] ?? s.contact.role})<br />
              {s.contact.email}{s.contact.phone ? ` · ${s.contact.phone}` : ''}<br />
              via {s.source === 'public' ? 'an invited link' : 'the admin app'}
            </p>
          </div>
          <div className={styles.detailSection}>
            <p className={styles.detailLabel}>Location</p>
            <p className={styles.detailValue}>
              {s.location.address.street}<br />
              {s.location.address.city}, {s.location.address.state} {s.location.address.zip}<br />
              Type: {s.location.type}
              {s.fleetId ? ' · fleet-linked' : ''}<br />
              Connection: {chargerConnectivityLabel(s.connectivity)}
            </p>
          </div>
        </div>

        <div className={styles.detailSection}>
          <p className={styles.detailLabel}>Chargers ({s.chargers.length})</p>
          <table className={styles.chargerTable}>
            <thead>
              <tr><th>Identity</th><th>Vendor / model</th><th>Connectors</th><th>kW</th></tr>
            </thead>
            <tbody>
              {s.chargers.map((c) => (
                <tr key={c.identity}>
                  <td>
                    <code>{c.identity}</code>
                    {dupByIdentity.has(c.identity) && (
                      <span className={styles.dupBadge}>
                        {dupByIdentity.get(c.identity) === 'existing_charger'
                          ? 'exists on network'
                          : 'in another submission'}
                      </span>
                    )}
                  </td>
                  <td>{[c.vendor, c.model].filter(Boolean).join(' ') || '—'}</td>
                  <td>{c.connector_types.join(', ')}</td>
                  <td>{c.max_power_kw ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <a href={`/deployments/${s.id}`}>Open commissioning and launch review</a>
        {live && (
          <>
            <div className={styles.detailSection}>
              <p className={styles.detailLabel}>Password-authenticated commissioning</p>
              <p>Authorization creates commissioning units. Passwords are available only on the signed-in deployment page. A separate human launch approval is required.</p>
            </div>

            {isSuper && (
              <div className={styles.field}>
                <span className={styles.label}>
                  Target tenant id <span className={styles.muted}>(optional — defaults to the submission&rsquo;s or yours)</span>
                </span>
                <input
                  className={styles.input}
                  value={tenantOverride}
                  onChange={(e) => setTenantOverride(e.target.value)}
                  placeholder="ObjectId"
                />
              </div>
            )}

            <div className={styles.field}>
              <span className={styles.label}>
                Note to the submitter <span className={styles.muted}>(required for needs-info; emailed with every decision)</span>
              </span>
              <textarea
                className={styles.input}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
              />
            </div>

            <label className={styles.radioOption}>
              <input
                type="checkbox"
                checked={reinvite}
                onChange={(e) => setReinvite(e.target.checked)}
              />
              <span>
                <strong>On Request info, email a single-use edit link.</strong> The link
                reopens THIS submission pre-filled for the submitter to fix and resubmit
                (14-day expiry, revocable from the invites list).
              </span>
            </label>
          </>
        )}

        {submitError && <div className={styles.errorBanner}>{submitError}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Close
          </button>
          {live && (
            <>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={decide.isPending || !note.trim()}
                onClick={() => { setSubmitError(''); decide.mutate('needs_info'); }}
              >
                Request info
              </button>
              <button
                type="button"
                className={styles.dangerBtn}
                disabled={decide.isPending}
                onClick={() => { setSubmitError(''); decide.mutate('reject'); }}
              >
                Reject
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={decide.isPending}
                onClick={() => { setSubmitError(''); decide.mutate('approve'); }}
              >
                {decide.isPending ? 'Working…' : 'Approve & register'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
